import { Controller } from "@hotwired/stimulus"
import { setButtonLoading } from "../lib/button_loading"

const STATUS_VARIANTS = {
  success: "text-success",
  error: "text-danger",
  info: "text-muted"
}

const RESPONSE_STATUS_CONFIG = {
  200: {
    variant: "success",
    message: "Test webhook sent successfully."
  },
  202: {
    variant: "success",
    message: "Test webhook request accepted by the destination service."
  },
  204: {
    variant: "success",
    message: "Test webhook sent successfully."
  },
  422: {
    variant: "error",
    message: "Failed to send test webhook. The destination service could not process the payload."
  },
  500: {
    variant: "error",
    message: "Failed to send test webhook due to a server error."
  }
}

export default class extends Controller {
  static targets = ["urlField", "payloadField", "status", "sendButton"]

  static values = {
    sendTestUrl: String
  }

  async sendTest(event) {
    event.preventDefault()

    const url = this.urlFieldTarget?.value?.trim()
    const payloadTemplate = this.payloadFieldTarget?.value ?? ""
    const trimmedPayloadTemplate = payloadTemplate.trim()

    this.clearStatus()

    if (!url) {
      this.updateStatus("URL is required to send a test.", "error")
      return
    }

    if (!trimmedPayloadTemplate) {
      this.updateStatus("Payload template is required to send a test.", "error")
      return
    }

    this.setLoadingState(true)

    try {
      const response = await fetch(this.sendTestUrlValue, {
        method: "POST",
        headers: this.fetchHeaders(),
        body: JSON.stringify({
          outgoing_webhook: {
            url,
            payload_template: payloadTemplate
          }
        })
      })

      if (response.ok) {
        const statusConfig = this.responseStatusConfig(response.status)
        const fallbackMessage = statusConfig?.message || "Test webhook sent successfully."
        const message = await this.extractMessage(response, fallbackMessage)
        const variant = statusConfig?.variant || "success"
        this.updateStatus(message, variant)
      } else {
        const statusConfig = this.responseStatusConfig(response.status)
        const error = await this.extractMessage(
          response,
          statusConfig?.message || `Failed to send test webhook (HTTP ${response.status}).`
        )
        const variant = statusConfig?.variant || "error"
        this.updateStatus(error, variant)
      }
    } catch (error) {
      this.updateStatus(`Unable to send test webhook: ${error.message}`, "error")
    } finally {
      this.setLoadingState(false)
    }
  }

  // Private helpers

  fetchHeaders() {
    return {
      "Content-Type": "application/json",
      "X-CSRF-Token": this.csrfToken()
    }
  }

  csrfToken() {
    const metaTag = document.querySelector('meta[name="csrf-token"]')
    return metaTag?.getAttribute("content") || ""
  }

  async extractMessage(response, fallback) {
    try {
      const contentType = response.headers?.get?.("content-type") || ""
      if (!contentType.toLowerCase().includes("application/json")) {
        return fallback
      }

      const data = await response.clone().json()
      if (data?.message) {
        return data.message
      }
    } catch (error) {
      // Ignore parse errors; we'll use the fallback message.
    }
    return fallback
  }

  // BUTTON-8: Send Test keeps its label while the request runs — spinner in the
  // icon's place, aria-busy, no second click — through the shared helper.
  setLoadingState(isLoading) {
    if (!this.hasSendButtonTarget) return

    setButtonLoading(this.sendButtonTarget, isLoading)
  }

  updateStatus(message, variant) {
    if (!this.hasStatusTarget) return

    const classes = Object.values(STATUS_VARIANTS)
    this.statusTarget.classList.remove(...classes)
    this.statusTarget.classList.add(STATUS_VARIANTS[variant] || STATUS_VARIANTS.info)
    this.statusTarget.textContent = message
    this.statusTarget.classList.remove("d-none")
  }

  clearStatus() {
    if (!this.hasStatusTarget) return

    const classes = Object.values(STATUS_VARIANTS)
    this.statusTarget.classList.remove(...classes)
    this.statusTarget.classList.add("d-none")
    this.statusTarget.textContent = ""
  }

  responseStatusConfig(status) {
    return RESPONSE_STATUS_CONFIG[status] || null
  }
}
